<script setup lang="ts">
import { Order, OrdersnapShot } from '@/@types/order'
import { Card, CardContent } from '@/components/ui/card'

import { Badge } from '@/components/ui/badge'
import { useForm } from '@inertiajs/vue3'
import { Ticket } from 'lucide-vue-next'
import { computed } from 'vue'
import ParticipantInfoBanner from '../../tickets-management/ParticipantInfoBanner.vue'
import TicketItem from '../../tickets-management/TicketItem.vue'
import TicketWrapper from '../../tickets-management/TicketWrapper.vue'

interface OrderShow extends Order {
    order_snap_shot: OrdersnapShot
}

const props = defineProps<{ order: OrderShow }>()

const order = computed(() => props.order)

const orderWithoutRevokedMailings = computed(() => ({
    ...order.value,
    items: order.value.items.map((item) => ({
        ...item,
        mailings: item.mailings.filter((mailing) => mailing.status !== 'canceled'),
    })),
}))

const orderWithRevokedMailings = computed(() => ({
    ...order.value,
    items: order.value.items.map((item) => ({
        ...item,
        mailings: item.mailings.filter((mailing) => mailing.status === 'canceled'),
    })),
}))

const form = useForm({})

function removeTicket(mailingId: number) {
    form.put(`/admin/mailings/${mailingId}/cancel`, {
        only: ['flash', 'order'],
    })
}

// const formUncancel = useForm({})
// const typeToUncancel = ref<string>('')
</script>

<template>
    <Card class="border-transparent pt-6">
        <CardContent>
            <h2 class="font-sans text-2xl font-semibold text-gray-800">{{ $t('Tickets') }}</h2>
            <p class="mb-3 text-sm">{{ $t('To validate tickets, associate the participant.') }}</p>

            <div v-if="order.status === 'paid' || order.status === 'released'" class="space-y-4">
                <div v-for="orderItem in orderWithoutRevokedMailings.items" :key="`${orderItem.ticket_id}-new`" class="space-y-4">
                    <TicketWrapper
                        v-for="(ticket, index) in orderItem.effective_quantity"
                        :key="`${ticket}-${orderItem.ticket_id}-ticket-new`"
                        class="rounded-[10px] px-4 py-6 shadow-[0_12px_12px_0px_rgba(12,12,13,.05)]"
                    >
                        <TicketItem
                            :mailing="orderItem.mailings[index]"
                            :order-item="orderItem"
                            :status="order.status"
                            @removeTicket="removeTicket"
                            has-cancel-ticket
                            :order="order"
                            is-admin
                        />

                        <ParticipantInfoBanner
                            v-if="orderItem.mailings[index]"
                            :mailing="orderItem.mailings[index]"
                            :orderItem="orderItem"
                            @delete="removeTicket"
                            :status="order.status"
                            :order="order"
                        />
                    </TicketWrapper>
                </div>

                <div v-for="orderItem in orderWithRevokedMailings.items" :key="`${orderItem.ticket_id}-revoked`" class="space-y-4">
                    <TicketWrapper
                        v-for="mailing in orderItem.mailings"
                        :key="`${mailing.id}-revoked`"
                        class="rounded-[10px] px-4 py-6 shadow-[0_12px_12px_0px_rgba(12,12,13,.05)]"
                    >
                        <TicketItem :mailing="mailing" :order-item="orderItem" :status="mailing.status" :order="order" />

                        <ParticipantInfoBanner
                            v-if="mailing"
                            :mailing="mailing"
                            :orderItem="orderItem"
                            :hasActions="false"
                            :status="order.status"
                            :order="order"
                        />
                    </TicketWrapper>
                </div>

                <div v-for="orderItem in order.items" :key="`${orderItem.ticket_id}-canceled`" class="space-y-4">
                    <TicketWrapper
                        v-for="ticket in orderItem.canceled_quantity"
                        :key="`${ticket}-canceled`"
                        class="rounded-[10px] px-4 py-6 shadow-[0_12px_12px_0px_rgba(12,12,13,.05)]"
                    >
                        <div class="flex w-full cursor-pointer flex-wrap items-center justify-between gap-4">
                            <div class="flex items-center gap-2 font-semibold">
                                <div class="flex size-11 items-center justify-center rounded-full bg-cyan-50 text-cyan-700">
                                    <Ticket />
                                </div>
                                <div class="flex flex-wrap items-center gap-x-2">
                                    {{ $t(orderItem.ticket.category) }}
                                </div>
                            </div>

                            <div class="flex items-center gap-4">
                                <Badge variant="canceled" class="cursor-default">{{ $t('canceled') }}</Badge>

                                <!-- <Dialog>
                                    <DialogOverlay class="z-60 bg-black/10" />
                                    <DialogTrigger class="cursor-pointer" as-child>
                                        <Button size="sm" class="bg-green-50 text-green-600 hover:bg-green-50/90">
                                            {{ $t('Uncancel ticket') }}
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent class="font-inter p-8 text-gray-800">
                                        <DialogHeader class="flex gap-2">
                                            <DialogTitle class="text-2xl">{{ $t('Are you sure you want to uncancel ticket') }}</DialogTitle>
                                        </DialogHeader>

                                        <div>
                                            <Label required for="type-uncancel">{{ $t("Please type 'REVERTER' to confirm.") }}</Label>
                                            <Input
                                                v-model="typeToUncancel"
                                                type="text"
                                                id="type-uncancel"
                                                @input="(e: Event) => (typeToUncancel = (e.target as HTMLInputElement).value.toUpperCase())"
                                            />
                                        </div>

                                        <DialogFooter class="pt-2">
                                            <form
                                                @submit.prevent="
                                                    formUncancel.post(`/admin/items/${orderItem.id}/restore_invalidation`, { preserveState: false })
                                                "
                                                class="flex w-full flex-col gap-6"
                                            >
                                                <div class="flex justify-between">
                                                    <DialogClose>
                                                        <Button
                                                            @click="typeToUncancel = ''"
                                                            type="button"
                                                            variant="ghost"
                                                            class="text-primary hover:text-primary hover:bg-primary/3 min-w-2/12"
                                                        >
                                                            {{ $t('Back') }}
                                                        </Button>
                                                    </DialogClose>
                                                    <Button :disabled="formUncancel.processing || typeToUncancel !== 'REVERTER'" class="min-w-2/12">
                                                        <template v-if="!formUncancel.processing">
                                                            {{ $t('Uncancel ticket') }}
                                                        </template>
                                                        <template v-else>
                                                            <span>
                                                                <Loader2Icon class="animate-spin" />
                                                            </span>
                                                        </template>
                                                    </Button>
                                                </div>
                                            </form>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog> -->
                            </div>
                        </div>
                    </TicketWrapper>
                </div>
            </div>

            <div v-else v-for="orderItem in order.items" :key="`${orderItem.ticket_id}-pending`" class="space-y-4">
                <TicketWrapper v-for="(ticket, index) in orderItem.effective_quantity" :key="`${ticket}-${orderItem.ticket_id}`">
                    <TicketItem :mailing="orderItem.mailings[index]" :order-item="orderItem" :status="order.status" :order="order" is-admin />
                </TicketWrapper>
            </div>
        </CardContent>
    </Card>
</template>
